import { Component, computed, input, output } from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

export interface TaskViewSettingOption {
  readonly key: string;
  readonly labelKey: string;
}

export interface TaskViewSettingChoiceSection {
  readonly kind: 'choice';
  readonly key: string;
  readonly labelKey: string;
  readonly toggleable?: boolean;
  readonly options: readonly TaskViewSettingOption[];
}

export interface TaskViewSettingToggleSection {
  readonly kind: 'toggles';
  readonly key: string;
  readonly labelKey: string;
  readonly toggleable?: boolean;
  readonly options: readonly TaskViewSettingOption[];
}

export type TaskViewSettingSection = TaskViewSettingChoiceSection | TaskViewSettingToggleSection;
export type TaskViewSettingValue = string | boolean;
export type TaskViewSettingValues = Readonly<Record<string, TaskViewSettingValue>>;

export interface TaskViewSettingChange {
  readonly key: string;
  readonly value: TaskViewSettingValue;
}

export interface TaskViewGroupChange {
  readonly key: string;
  readonly enabled: boolean;
}

/**
 * PROPOZYCJA — DO WERYFIKACJI: task-specific, presentational settings menu.
 * The caller owns state and supplies sections; list and details can use different fields.
 * @summary Renders translated single-choice and toggle settings without coupling to list filters.
 * @selector proffeo-task-view-settings
 */
@Component({
  selector: 'proffeo-task-view-settings',
  templateUrl: './task-view-settings.component.html',
  styleUrl: './task-view-settings.component.scss',
  imports: [MatCheckboxModule, MatIconModule, MatMenuModule, MatRadioModule, MatTooltipModule, TranslatePipe]
})
export class TaskViewSettingsComponent {
  protected readonly allEnabled = computed(() => {
    const toggleable = this.sections().filter(section => section.toggleable);
    if (toggleable.length === 0) {
      return false;
    }

    const enabled = this.groupEnabled();
    return toggleable.every(section => enabled[section.key] !== false);
  });

  protected readonly allIndeterminate = computed(() => {
    const toggleable = this.sections().filter(section => section.toggleable);
    const enabled = this.groupEnabled();
    const someEnabled = toggleable.some(section => enabled[section.key] !== false);
    return someEnabled && !this.allEnabled();
  });

  /** Translation key for the trigger and menu's accessible name. */
  public readonly labelKey = input.required<string>();
  /** Choice and toggle groups configured by the consumer. */
  public readonly sections = input.required<readonly TaskViewSettingSection[]>();
  /** Current values keyed by section key (choice) or option key (toggle). */
  public readonly values = input.required<TaskViewSettingValues>();
  /** Optional enabled state of toggleable groups, keyed by section key. */
  public readonly groupEnabled = input<Readonly<Record<string, boolean>>>({});
  /** Shows a Show / hide all checkbox that emits `groupToggled` with key `all`. */
  public readonly bulkToggle = input(false);
  /** Requests one change; the consumer remains the source of truth. */
  public readonly settingChanged = output<TaskViewSettingChange>();
  /** Requests a whole thematic group to be enabled or disabled. */
  public readonly groupToggled = output<TaskViewGroupChange>();

  protected changeGroup(groupKey: string, event: MatCheckboxChange): void {
    this.groupToggled.emit({ key: groupKey, enabled: event.checked });
  }

  protected changeChoice(sectionKey: string, event: MatRadioChange): void {
    if (typeof event.value === 'string') {
      this.settingChanged.emit({ key: sectionKey, value: event.value });
    }
  }

  protected changeToggle(optionKey: string, event: MatCheckboxChange): void {
    this.settingChanged.emit({ key: optionKey, value: event.checked });
  }

  protected changeAll(event: MatCheckboxChange): void {
    this.groupToggled.emit({ key: 'all', enabled: event.checked });
  }
}
